import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { sync } from '#app/appSlice';
import { trasladarPortada } from '#components/manager/puerto/portada';
import { Boton } from '#components/mobile/ui/Boton';
import { color, texto } from '#components/mobile/ui/tokens';
import { useCompartir } from '#components/pareja/useCompartir';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { loadPrefs } from '#prefs/prefsSlice';
import { useDispatch } from '#redux';

type Paso = 'quieto' | 'subiendo' | 'confirmar' | 'quitando';

/**
 * Ajustes → «Amarre»: dónde se guarda el presupuesto abierto. Uno que está
 * solo en este dispositivo se sube a la nube; el dueño de uno de la nube
 * puede dejarlo solo en este dispositivo (se borra del servidor).
 */
export function AmarreSettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [id] = useMetadataPref('id');
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const { esDueno } = useCompartir();
  const [paso, setPaso] = useState<Paso>('quieto');
  const [aviso, setAviso] = useState<string | null>(null);

  const subir = async () => {
    setPaso('subiendo');
    setAviso(null);
    const r = await send('upload-budget');
    if (r?.error) {
      setAviso(t('Could not upload it. Check your connection and try again.'));
      setPaso('quieto');
      return;
    }
    const prefs = await send('load-prefs');
    if (id && prefs?.cloudFileId) {
      trasladarPortada(id, prefs.cloudFileId);
    }
    await dispatch(loadPrefs());
    void dispatch(sync());
    setPaso('quieto');
  };

  const quitar = async () => {
    setPaso('quitando');
    setAviso(null);
    const r = await send('rumbo-dejar-solo-local');
    if (r?.error) {
      setAviso(
        t(
          'Could not remove it from the cloud. Check your connection and try again.',
        ),
      );
      setPaso('confirmar');
      return;
    }
    if (id && cloudFileId) {
      trasladarPortada(cloudFileId, id);
    }
    await dispatch(loadPrefs());
    setPaso('quieto');
  };

  const textoAviso = aviso && (
    <Text role="alert" style={{ ...texto.secundario, color: color.bad }}>
      {aviso}
    </Text>
  );

  if (!cloudFileId) {
    return (
      <View style={{ gap: 12 }} data-testid="amarre-local">
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          <Trans>
            This boat is only on this phone. Upload it to the cloud to see it on
            other devices, share it and not lose it if you change phones.
          </Trans>
        </Text>
        <Boton
          bloque
          data-testid="amarre-subir"
          isDisabled={paso === 'subiendo'}
          onPress={() => void subir()}
        >
          {paso === 'subiendo' ? t('Uploading…') : t('Upload to the cloud')}
        </Boton>
        {textoAviso}
      </View>
    );
  }

  return (
    <View style={{ gap: 12 }} data-testid="amarre-nube">
      <Text style={{ ...texto.secundario, color: color.fg2 }}>
        <Trans>
          This boat is on this phone and in the cloud: you see it on all your
          devices and you do not lose it if you change phones.
        </Trans>
      </Text>
      {!esDueno ? (
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          <Trans>Only its owner can take it out of the cloud.</Trans>
        </Text>
      ) : paso === 'quieto' ? (
        <Boton
          bloque
          variante="fantasma"
          data-testid="amarre-quitar"
          onPress={() => setPaso('confirmar')}
        >
          <Trans>Leave only on this phone</Trans>
        </Boton>
      ) : (
        <View style={{ gap: 10 }}>
          <Text style={{ ...texto.secundario, color: color.warn }}>
            <Trans>
              It will be deleted from the server and from your other devices,
              and it will stop being shared. Only this phone will keep it.
            </Trans>
          </Text>
          <Boton
            bloque
            variante="peligro"
            data-testid="amarre-confirmar"
            isDisabled={paso === 'quitando'}
            onPress={() => void quitar()}
          >
            <Trans>Yes, leave it only here</Trans>
          </Boton>
          <Boton
            bloque
            variante="fantasma"
            isDisabled={paso === 'quitando'}
            onPress={() => setPaso('quieto')}
          >
            <Trans>Cancel</Trans>
          </Boton>
        </View>
      )}
      {textoAviso}
    </View>
  );
}
